import { euro } from '../../core/format/currency'
import { SITE_URL } from '../../data/seo'
import { slugify } from '../editing'
import type { SiteSettings } from '../../core/types/site'

/** Alle {tokens} voor marketingteksten, uit de instellingen zoals Sarah ze nu heeft staan. */
export const marketingTokens = (site: SiteSettings): Record<string, string> => ({
  firstName: site.founder.firstName,
  name: site.founder.name,
  city: site.salon.city,
  street: site.salon.street,
  locality: site.salon.locality,
  address: site.salon.address,
  email: site.salon.email,
  phone: site.salon.phone,
  years: String(site.policy.yearsExperience),
  responseHours: String(site.policy.responseHours),
  consultName: site.policy.consult.name,
  consultMinutes: String(site.policy.consult.minutes),
  consultPrice: euro(site.policy.consult.price),
  freeShippingFrom: euro(site.policy.freeShippingFrom),
  giftCardValidity: giftCardValidity(site.policy.giftCardValidMonths),
  website: SITE_URL.replace(/^https?:\/\//, ''),
})

const YEAR_WORDS: Record<number, string> = { 1: 'een', 2: 'twee', 3: 'drie' }

/** "twee jaar geldig" / "18 maanden geldig" — zelfde regel als policyCopy, maar uit de concept-instellingen. */
function giftCardValidity(months: number) {
  if (months % 12 !== 0) return `${months} maanden geldig`
  const years = months / 12
  return `${YEAR_WORDS[years] ?? years} jaar geldig`
}

/** Vult {tokens} in; onbekende tokens blijven zichtbaar staan, zodat Sarah ze opmerkt. */
export const fillTokens = (text: string, tokens: Record<string, string>) =>
  text.replace(/\{(\w+)\}/g, (match, key: string) => tokens[key] ?? match)

/**
 * Beelden in ontwerpen lopen via een beeldproxy die CORS toestaat: zo kan de
 * browser het ontwerp als PNG opslaan, en omzeilen we de hotlink-beveiliging
 * van de oude webshop. Lokale uploads (blob:, data:) en paden op ons eigen domein
 * (`/…`, geen `//host`) blijven zoals ze zijn: die zijn al zelfde origin, en de proxy
 * kan een relatief pad niet eens ophalen.
 */
export const designImage = (src: string, width = 1400, format: 'png' | 'jpg' = 'png') => {
  if (!src || src.startsWith('blob:') || src.startsWith('data:')) return src
  if (src.startsWith('/') && !src.startsWith('//')) return src
  const q = format === 'jpg' ? '&q=88' : ''
  return `https://images.weserv.nl/?url=${encodeURIComponent(src.replace(/^https?:\/\//, ''))}&w=${width}&output=${format}${q}`
}

export const qrImage = (url: string, fg: string, bg: string) =>
  designImage(
    `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=0&format=png&color=${fg.slice(1)}&bgcolor=${bg.slice(1)}&data=${encodeURIComponent(url)}`,
    600,
  )

/** Bestandsnaam voor een download; het voorvoegsel volgt de salonnaam uit de instellingen. */
export const designFileName = (name: string, brand = 'beauty bewust') => `${slugify(brand)}-${slugify(name)}.png`

/** Opent een PNG-download van een DOM-knoop op ware grootte. */
export async function downloadNode(node: HTMLElement, fileName: string, scale: number) {
  const { toPng } = await import('html-to-image')
  const dataUrl = await toPng(node, {
    pixelRatio: scale,
    cacheBust: false,
    // De schaal van de voorvertoning mag niet mee in het bestand.
    style: { transform: 'none', margin: '0' },
  })
  const a = document.createElement('a')
  a.href = dataUrl
  a.download = fileName
  a.click()
}
